/**
 * PortalRolleriPage.jsx — Sistem Ayarları › Portal Rolleri (backlog #417, 2026-10-14; tasarım #409 D2 / D10)
 * Route: /Management/SistemAyarlari/PortalRolleri   İzin: portal.rol.manage (kök + Admin)
 *
 * Firma portalının ROLLERİ: her rol, her modülde bir seviye (NONE / READ / WRITE / MANAGE) ve organizatör eylemleri verir.
 * Bir girişin etkin seviyesi = min(arayüz setinin tavanı, girişin bu firmadaki rollerinin en iyisi, firma kısıtı): burada
 * yalnızca rolün ne verdiği düzenlenir; tavan Portal Arayüzleri'nde, atama Firma Detay › Portal Kullanıcıları'ndadır.
 *
 * Sol: rol listesi (Türkçe / İngilizce adlar, "Sistem" rozeti: sistem rolleri yeniden adlandırılabilir, silinemez).
 * Sağ: seçili rolün modül × seviye tablosu ve organizatör eylemleri; "Kaydet" TAM seti gönderir, backend yalnızca farkı uygular.
 * Veri: services/portalRbacService.js → /portal-rbac/roller. Her yazma Yetki Yönetimi › Geçmiş'e düşer.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Container, Row, Col, Card, CardBody, CardHeader, Table, Badge, Button, Input, Label, FormGroup, FormText,
  Modal, ModalHeader, ModalBody, ModalFooter, Alert, Spinner,
} from "reactstrap";
import { Plus, Pencil, Trash2, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { getRoller, createRol, updateRol, setRolModuller, deleteRol } from "../../../services/portalRbacService";

const BOS_FORM = { kod: "", ad: "", adEn: "", aciklama: "", aciklamaEn: "", aktif: true };

function ayniKume(a, b) {
  if (a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
}

export default function PortalRolleriPage() {
  const { t, i18n } = useTranslation("ayarlar");
  const [veri, setVeri] = useState(null);
  const [hata, setHata] = useState(null);
  const [seciliId, setSeciliId] = useState(null);
  const [taslaklar, setTaslaklar] = useState({});    // rol id -> { moduller: {MODUL: SEVIYE}, eylemler: Set } (yalnızca düzenlenenler)
  const [kaydediyor, setKaydediyor] = useState(false);
  const [bilgi, setBilgi] = useState(null);
  const [islemHatasi, setIslemHatasi] = useState(null);
  const [modal, setModal] = useState(null);          // "yeni" | "duzenle" | "sil" | null
  const [form, setForm] = useState(BOS_FORM);
  const [yenile, setYenile] = useState(0);

  useEffect(() => { document.title = `${t("portalRolleri.baslik")} | FDS`; }, [t, i18n.language]);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect); yazmadan sonra yeniden okuma `yenile` sayacıyla.
  // Rol adları istek dilinde gelir: dil değişince yeniden okunur.
  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        const data = await getRoller();
        if (ignore) return;
        setVeri(data);
        setHata(null);
        setSeciliId((onceki) => (data.roller.some((r) => r.id === onceki) ? onceki : (data.roller[0]?.id ?? null)));
      } catch (e) {
        if (!ignore) setHata(e.message || t("portalRolleri.okunamadi"));
      }
    }
    run();
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yenile, i18n.language]);

  const secili = useMemo(() => veri?.roller?.find((r) => r.id === seciliId) ?? null, [veri, seciliId]);

  // Taslak: seçili rolün kendi değerleri, kullanıcı değiştirene kadar. Rol başına tutulur (başka role geçip dönünce kaybolmaz);
  // yazdıktan sonra ya da "Vazgeç" ile temizlenir. (Bir effect ile kopyalanmaz: state'i effect içinde kurmak yerine türetilir.)
  const baslangic = useMemo(
    () => (secili ? { moduller: { ...secili.moduller }, eylemler: new Set(secili.eylemler) } : null),
    [secili],
  );
  const taslak = secili ? (taslaklar[secili.id] ?? baslangic) : null;
  const setTaslak = (degistir) => {
    if (!secili) return;
    setTaslaklar((x) => ({ ...x, [secili.id]: degistir(x[secili.id] ?? baslangic) }));
  };
  const taslakAt = () => setTaslaklar((x) => { const y = { ...x }; if (secili) delete y[secili.id]; return y; });

  const moduller = useMemo(() => veri?.moduller ?? [], [veri]);
  const seviyeler = veri?.seviyeler ?? ["NONE", "READ", "WRITE", "MANAGE"];
  const eylemKatalogu = veri?.eylemKatalogu ?? [];

  const degisenModuller = useMemo(
    () => (secili && taslak ? moduller.filter((m) => taslak.moduller[m] !== secili.moduller[m]) : []),
    [secili, taslak, moduller],
  );
  const eylemDegisti = secili && taslak ? !ayniKume(taslak.eylemler, new Set(secili.eylemler)) : false;
  const kirli = degisenModuller.length > 0 || eylemDegisti;

  const kaydet = async () => {
    if (!secili || !taslak) return;
    setKaydediyor(true);
    setIslemHatasi(null);
    try {
      const sonuc = await setRolModuller(secili.id, taslak.moduller, [...taslak.eylemler]);
      const n = (sonuc.degisenModuller?.length ?? 0) + (sonuc.eklenenEylemler?.length ?? 0) + (sonuc.cikanEylemler?.length ?? 0);
      setBilgi(t("portalRolleri.kaydedildi", { n }));
      setTaslaklar({});
      setYenile((x) => x + 1);
    } catch (e) {
      setIslemHatasi(e.message || t("portalRolleri.kaydedilemedi"));
    } finally {
      setKaydediyor(false);
    }
  };

  const formAc = (tur) => {
    setIslemHatasi(null);
    if (tur === "yeni") setForm(BOS_FORM);
    if (tur === "duzenle" && secili) {
      setForm({
        kod: secili.kod, ad: secili.adTr ?? "", adEn: secili.adEn ?? "", aciklama: secili.aciklamaTr ?? "",
        aciklamaEn: secili.aciklamaEn ?? "", aktif: secili.aktif,
      });
    }
    setModal(tur);
  };

  const formKaydet = async () => {
    setKaydediyor(true);
    setIslemHatasi(null);
    try {
      if (modal === "yeni") {
        const rol = await createRol({
          kod: form.kod.trim(), ad: form.ad.trim(), adEn: form.adEn.trim() || undefined,
          aciklama: form.aciklama.trim() || undefined, aciklamaEn: form.aciklamaEn.trim() || undefined,
        });
        setBilgi(t("portalRolleri.olusturuldu", { ad: rol.adTr }));
        setModal(null);
        setSeciliId(rol.id);
        setYenile((x) => x + 1);
      } else if (modal === "duzenle" && secili) {
        await updateRol(secili.id, {
          ad: form.ad.trim(), adEn: form.adEn.trim() || null,
          aciklama: form.aciklama.trim() || null, aciklamaEn: form.aciklamaEn.trim() || null, aktif: form.aktif,
        });
        setBilgi(t("portalRolleri.guncellendi"));
        setModal(null);
        setYenile((x) => x + 1);
      }
    } catch (e) {
      setIslemHatasi(e.message || t("portalRolleri.kaydedilemedi"));
    } finally {
      setKaydediyor(false);
    }
  };

  const sil = async () => {
    if (!secili) return;
    setKaydediyor(true);
    setIslemHatasi(null);
    try {
      await deleteRol(secili.id);
      setBilgi(t("portalRolleri.silindi", { ad: secili.adTr }));
      setModal(null);
      setSeciliId(null);
      setYenile((x) => x + 1);
    } catch (e) {
      setIslemHatasi(e.message || t("portalRolleri.kaydedilemedi"));
    } finally {
      setKaydediyor(false);
    }
  };

  const silinebilir = secili && !secili.sistem && secili.baglantiSayisi === 0 && (secili.varsayilanGruplar?.length ?? 0) === 0;
  const eylemAdi = (e) => t(`portalRolleri.eylem.${e.kod}`, { defaultValue: e.ad });
  const formGecerli = form.ad.trim().length > 0 && (modal !== "yeni" || /^[A-Z][A-Z0-9_]{1,39}$/.test(form.kod.trim().toUpperCase()));

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("portalRolleri.baslik")} pageTitle={t("portalRolleri.ustBaslik")} />
        {hata && <Alert color="danger">{hata}</Alert>}
        {bilgi && <Alert color="success" toggle={() => setBilgi(null)} data-testid="portal-rol-bilgi">{bilgi}</Alert>}
        {!veri && !hata && <div className="text-center py-5"><Spinner color="primary" /></div>}

        {veri && (
          <Row>
            <Col lg={4}>
              <Card>
                <CardHeader className="d-flex align-items-center justify-content-between">
                  <h5 className="mb-0">{t("portalRolleri.rolListesi")}</h5>
                  <div className="d-flex gap-2">
                    <Button color="light" size="sm" onClick={() => setYenile((x) => x + 1)} title={t("portalRolleri.yenile")}>
                      <RefreshCw size={14} />
                    </Button>
                    <Button color="primary" size="sm" onClick={() => formAc("yeni")} data-testid="portal-rol-yeni">
                      <Plus size={14} className="me-1" />{t("portalRolleri.yeniRol")}
                    </Button>
                  </div>
                </CardHeader>
                <CardBody className="p-0">
                  <div className="list-group list-group-flush" data-testid="portal-rol-listesi">
                    {veri.roller.map((r) => (
                      <button
                        type="button" key={r.id} data-rol={r.kod}
                        className={`list-group-item list-group-item-action ${r.id === seciliId ? "active" : ""}`}
                        onClick={() => { setSeciliId(r.id); setBilgi(null); setIslemHatasi(null); }}
                      >
                        <div className="d-flex align-items-center justify-content-between gap-2">
                          <span className="fw-semibold">{r.ad}</span>
                          <span className="d-flex gap-1">
                            {r.sistem && <Badge color="secondary">{t("portalRolleri.sistem")}</Badge>}
                            {!r.aktif && <Badge color="warning">{t("portalRolleri.pasif")}</Badge>}
                          </span>
                        </div>
                        <div className="small opacity-75">
                          {r.adTr}{r.adEn ? ` / ${r.adEn}` : ""} · <code>{r.kod}</code>
                        </div>
                        <div className="small opacity-75">{t("portalRolleri.atama", { n: r.baglantiSayisi })}</div>
                      </button>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col lg={8}>
              {secili && taslak && (
                <Card>
                  <CardHeader className="d-flex align-items-start justify-content-between flex-wrap gap-2">
                    <div>
                      <h5 className="mb-1">{secili.ad} {secili.sistem && <Badge color="secondary" className="ms-1">{t("portalRolleri.sistem")}</Badge>}</h5>
                      <div className="text-muted small">{secili.aciklama}</div>
                      {secili.varsayilanGruplar?.length > 0 && (
                        <div className="small mt-1">{t("portalRolleri.varsayilan", { gruplar: secili.varsayilanGruplar.join(", ") })}</div>
                      )}
                    </div>
                    <div className="d-flex gap-2">
                      <Button color="light" size="sm" onClick={() => formAc("duzenle")} data-testid="portal-rol-duzenle">
                        <Pencil size={14} className="me-1" />{t("portalRolleri.adlariDuzenle")}
                      </Button>
                      <Button
                        color="danger" outline size="sm" disabled={!silinebilir} data-testid="portal-rol-sil"
                        title={silinebilir ? undefined : (secili.sistem ? t("portalRolleri.sistemSilinemez") : t("portalRolleri.kullanimdaSilinemez"))}
                        onClick={() => formAc("sil")}
                      >
                        <Trash2 size={14} className="me-1" />{t("portalRolleri.sil")}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardBody>
                    {islemHatasi && !modal && <Alert color="danger" className="small">{islemHatasi}</Alert>}
                    <Alert color="info" className="small">{t("portalRolleri.tavanNotu")}</Alert>

                    <h6 className="mb-2">{t("portalRolleri.modulSeviyeleri")}</h6>
                    <div className="table-responsive">
                      <Table size="sm" className="align-middle mb-3" data-testid="portal-rol-moduller">
                        <thead className="table-light">
                          <tr>
                            <th>{t("portalArayuzleri.modulBasligi")}</th>
                            <th style={{ width: 220 }}>{t("portalRolleri.seviye")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {moduller.map((m) => (
                            <tr key={m} className={taslak.moduller[m] !== secili.moduller[m] ? "table-warning" : undefined}>
                              <td>{t(`portalArayuzleri.modul.${m}`)}</td>
                              <td>
                                <Input
                                  type="select" bsSize="sm" value={taslak.moduller[m] ?? "NONE"} data-testid={`rol-modul-${m}`}
                                  onChange={(e) => setTaslak((x) => ({ ...x, moduller: { ...x.moduller, [m]: e.target.value } }))}
                                >
                                  {seviyeler.map((s) => <option key={s} value={s}>{t(`portalArayuzleri.seviye.${s}`)}</option>)}
                                </Input>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>

                    <h6 className="mb-1">{t("portalRolleri.eylemler")}</h6>
                    <div className="text-muted small mb-2">{t("portalRolleri.eylemAciklama")}</div>
                    <div className="row g-2 mb-3" data-testid="portal-rol-eylemler">
                      {eylemKatalogu.map((e) => (
                        <div className="col-md-6" key={e.kod}>
                          <FormGroup check className="mb-0">
                            <Input
                              type="checkbox" id={`eylem-${e.kod}`} data-testid={`rol-eylem-${e.kod}`}
                              checked={taslak.eylemler.has(e.kod)}
                              onChange={(ev) => setTaslak((x) => {
                                const k = new Set(x.eylemler);
                                if (ev.target.checked) k.add(e.kod); else k.delete(e.kod);
                                return { ...x, eylemler: k };
                              })}
                            />
                            <Label check for={`eylem-${e.kod}`} className="small">
                              {eylemAdi(e)}
                              <span className="text-muted ms-1">({t(`portalArayuzleri.modul.${e.modul}`)} · {t(`portalArayuzleri.seviye.${e.gerekenSeviye}`)})</span>
                            </Label>
                          </FormGroup>
                        </div>
                      ))}
                    </div>

                    <div className="d-flex align-items-center gap-2">
                      <Button color="primary" disabled={!kirli || kaydediyor} onClick={kaydet} data-testid="portal-rol-kaydet">
                        {kaydediyor ? <Spinner size="sm" /> : t("portalRolleri.kaydet")}
                      </Button>
                      <Button color="light" disabled={!kirli || kaydediyor} onClick={taslakAt}>
                        {t("portalRolleri.vazgec")}
                      </Button>
                      {kirli && <Badge color="warning" pill>{t("portalRolleri.degisiklik", { n: degisenModuller.length + (eylemDegisti ? 1 : 0) })}</Badge>}
                      <Badge color="light" className="text-muted ms-auto">{t("portalRolleri.atama", { n: secili.baglantiSayisi })}</Badge>
                    </div>
                  </CardBody>
                </Card>
              )}
            </Col>
          </Row>
        )}

        {/* Yeni rol / adları düzenle */}
        <Modal isOpen={modal === "yeni" || modal === "duzenle"} toggle={() => setModal(null)}>
          <ModalHeader toggle={() => setModal(null)}>
            {modal === "yeni" ? t("portalRolleri.yeniRol") : t("portalRolleri.adlariDuzenle")}
          </ModalHeader>
          <ModalBody>
            {islemHatasi && <Alert color="danger" className="small">{islemHatasi}</Alert>}
            {modal === "yeni" && (
              <FormGroup>
                <Label for="rol-kod">{t("portalRolleri.kod")}</Label>
                <Input id="rol-kod" value={form.kod} placeholder="ROL_KODU" data-testid="rol-form-kod"
                  onChange={(e) => setForm((f) => ({ ...f, kod: e.target.value.toUpperCase() }))} />
                <FormText>{t("portalRolleri.kodIpucu")}</FormText>
              </FormGroup>
            )}
            <FormGroup>
              <Label for="rol-ad">{t("portalRolleri.adTr")}</Label>
              <Input id="rol-ad" value={form.ad} maxLength={100} data-testid="rol-form-ad"
                onChange={(e) => setForm((f) => ({ ...f, ad: e.target.value }))} />
            </FormGroup>
            <FormGroup>
              <Label for="rol-ad-en">{t("portalRolleri.adEn")}</Label>
              <Input id="rol-ad-en" value={form.adEn} maxLength={100} data-testid="rol-form-ad-en"
                onChange={(e) => setForm((f) => ({ ...f, adEn: e.target.value }))} />
              <FormText>{t("portalRolleri.ingilizceBos")}</FormText>
            </FormGroup>
            <FormGroup>
              <Label for="rol-acik">{t("portalRolleri.aciklamaTr")}</Label>
              <Input id="rol-acik" type="textarea" rows={2} value={form.aciklama} maxLength={500}
                onChange={(e) => setForm((f) => ({ ...f, aciklama: e.target.value }))} />
            </FormGroup>
            <FormGroup>
              <Label for="rol-acik-en">{t("portalRolleri.aciklamaEn")}</Label>
              <Input id="rol-acik-en" type="textarea" rows={2} value={form.aciklamaEn} maxLength={500}
                onChange={(e) => setForm((f) => ({ ...f, aciklamaEn: e.target.value }))} />
            </FormGroup>
            {modal === "duzenle" && (
              <FormGroup check>
                <Input id="rol-aktif" type="checkbox" checked={form.aktif} onChange={(e) => setForm((f) => ({ ...f, aktif: e.target.checked }))} />
                <Label check for="rol-aktif">{t("portalRolleri.aktif")}</Label>
                <FormText className="d-block">{t("portalRolleri.aktifIpucu")}</FormText>
              </FormGroup>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setModal(null)}>{t("portalRolleri.vazgec")}</Button>
            <Button color="primary" disabled={!formGecerli || kaydediyor} onClick={formKaydet} data-testid="rol-form-kaydet">
              {kaydediyor ? <Spinner size="sm" /> : (modal === "yeni" ? t("portalRolleri.olustur") : t("portalRolleri.kaydet"))}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Sil */}
        <Modal isOpen={modal === "sil"} toggle={() => setModal(null)}>
          <ModalHeader toggle={() => setModal(null)}>{t("portalRolleri.sil")}</ModalHeader>
          <ModalBody>
            {islemHatasi && <Alert color="danger" className="small">{islemHatasi}</Alert>}
            {t("portalRolleri.silOnay", { ad: secili?.adTr })}
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setModal(null)}>{t("portalRolleri.vazgec")}</Button>
            <Button color="danger" disabled={kaydediyor} onClick={sil} data-testid="rol-sil-onayla">{t("portalRolleri.sil")}</Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
}
